import type { Feature, Geometry, Position } from 'geojson';
import * as Utils from '../utils';

/** WGS84 — map draw, coordinate form, and logical editing use longitude / latitude (degrees). */
export const DEFAULT_FEATURE_GEOMETRY_SRID = 4326;

/** Geobox vector create/update expects Web Mercator geometry by default (EPSG:3857). */
export const GEOBOX_API_GEOMETRY_SRID = 3857;

/** Half-world Web Mercator extent (meters). */
const WEB_MERCATOR_MAX = 20037508.342789244;

/**
 * True when coordinates look like EPSG:3857 meters (not WGS84 degrees).
 * Small values inside the degree envelope (e.g. 134, 172) are treated as degrees —
 * converting them as meters wrongly yields ~0.001° near the origin.
 */
export function isLikelyWebMercatorMeters(x: number, y: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  if (ax <= 180 && ay <= 90) return false;
  const maxAbs = Math.max(ax, ay);
  if (maxAbs < 1000) return false;
  if (ax > WEB_MERCATOR_MAX || ay > WEB_MERCATOR_MAX) return false;
  return true;
}

/** Convert a draw feature collection from EPSG:3857 to WGS84 when needed. */
export function normalizeDrawFeatureCollectionToWgs84(collection: {
  features: Feature[];
}): { features: Feature[]; changed: boolean } {
  let changed = false;
  const features = collection.features.map((f) => {
    if (!f.geometry) return f;
    const nextGeom = geometryToLngLat(f.geometry);
    const prev = JSON.stringify((f.geometry as { coordinates?: unknown }).coordinates);
    const next = JSON.stringify((nextGeom as { coordinates?: unknown }).coordinates);
    if (prev !== next) {
      changed = true;
      return { ...f, geometry: nextGeom };
    }
    return f;
  });
  return { features, changed };
}

export function positionToLngLat(coord: Position): Position {
  const x = coord[0];
  const y = coord[1];
  const rest = coord.slice(2);
  if (isLikelyWebMercatorMeters(x, y)) {
    const [lng, lat] = Utils.webMercatorToLngLat(x, y);
    return rest.length ? [lng, lat, ...rest] : [lng, lat];
  }
  return [...coord];
}

/** Always treat coordinates as EPSG:3857 meters and convert to WGS84. */
export function positionWebMercatorToLngLat(coord: Position): Position {
  const x = coord[0];
  const y = coord[1];
  const rest = coord.slice(2);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return [...coord];
  const [lng, lat] = Utils.webMercatorToLngLat(x, y);
  return rest.length ? [lng, lat, ...rest] : [lng, lat];
}

function walkCoordinates(coords: unknown, mapPos: (p: Position) => Position): unknown {
  if (!Array.isArray(coords)) return coords;
  if (typeof coords[0] === 'number') {
    return mapPos(coords as Position);
  }
  return coords.map((c) => walkCoordinates(c, mapPos));
}

export function geometryToLngLat(geometry: Geometry): Geometry {
  const g = JSON.parse(JSON.stringify(geometry)) as Geometry;
  const mapPos = (p: Position) => positionToLngLat(p);
  (g as { coordinates: unknown }).coordinates = walkCoordinates(
    (g as { coordinates: unknown }).coordinates,
    mapPos
  );
  return g;
}

/** Convert server snap geometries that always arrive in EPSG:3857 to WGS84. */
export function geometryWebMercatorToLngLat(geometry: Geometry): Geometry {
  const g = JSON.parse(JSON.stringify(geometry)) as Geometry;
  if ((g as { type?: string }).type === 'GeometryCollection') return g;
  const mapPos = (p: Position) => positionWebMercatorToLngLat(p);
  (g as { coordinates: unknown }).coordinates = walkCoordinates(
    (g as { coordinates: unknown }).coordinates,
    mapPos
  );
  return g;
}

function storagePosition(
  lng: number,
  lat: number,
  apiSrid: number,
  hasZ: boolean
): Position {
  if (apiSrid === DEFAULT_FEATURE_GEOMETRY_SRID) {
    return hasZ ? [lng, lat, 0] : [lng, lat];
  }
  const merc = Utils.lngLatToWebMercator(lng, lat);
  return hasZ ? [merc[0], merc[1], 0] : merc;
}

function mapLngLatPositionsToStorage(
  coords: unknown,
  apiSrid: number,
  hasZ: boolean
): unknown {
  if (!Array.isArray(coords)) return coords;
  if (typeof coords[0] === 'number') {
    const p = positionToLngLat(coords as Position);
    return storagePosition(p[0], p[1], apiSrid, hasZ);
  }
  return coords.map((c) => mapLngLatPositionsToStorage(c, apiSrid, hasZ));
}

function layerStorageGeometryType(layerType: string): string {
  switch (layerType) {
    case 'Polyline':
      return 'MultiLineString';
    case 'Polygon':
      return 'MultiPolygon';
    case 'Point':
      return 'Point';
    default:
      return 'MultiPoint';
  }
}

/** Normalize a sketch/read model feature to WGS84 degrees for UI and gl-draw. */
export function normalizeSketchFeatureToWgs84(feature: Feature): Feature {
  if (!feature.geometry) return feature;
  return {
    ...feature,
    geometry: geometryToLngLat(feature.geometry),
    properties: feature.properties ?? {},
  };
}

/**
 * Builds a layer-shaped Feature for Geobox createFeature / updateFeature.
 * Sketch is always interpreted as WGS84 [longitude, latitude]; output uses apiSrid (3857 by default).
 */
export function prepareSketchFeatureForCreate(
  sketch: Feature,
  layerType: string,
  hasZ: boolean,
  apiSrid: number = GEOBOX_API_GEOMETRY_SRID
): Feature {
  const geometry = geometryToLngLat(sketch.geometry!);
  const storageType = layerStorageGeometryType(layerType);
  let coordinates: unknown = [];

  switch (geometry.type) {
    case 'Point': {
      const raw = geometry.coordinates as Position;
      const lng = raw[0];
      const lat = raw[1];
      const p: Position = isLikelyWebMercatorMeters(lng, lat)
        ? positionToLngLat(raw)
        : [lng, lat];
      coordinates =
        storageType === 'Point'
          ? storagePosition(p[0], p[1], apiSrid, hasZ)
          : [storagePosition(p[0], p[1], apiSrid, hasZ)];
      break;
    }
    case 'LineString':
      coordinates = [
        (geometry.coordinates as Position[]).map((p) => {
          const ll = positionToLngLat(p);
          return storagePosition(ll[0], ll[1], apiSrid, hasZ);
        }),
      ];
      break;
    case 'Polygon':
      coordinates = [
        (geometry.coordinates as Position[][]).map((ring) =>
          (ring as Position[]).map((p) => {
            const ll = positionToLngLat(p);
            return storagePosition(ll[0], ll[1], apiSrid, hasZ);
          })
        ),
      ];
      break;
    case 'MultiPoint':
    case 'MultiLineString':
    case 'MultiPolygon':
      coordinates = mapLngLatPositionsToStorage(geometry.coordinates, apiSrid, hasZ);
      break;
    default:
      if ('coordinates' in geometry) {
        coordinates = mapLngLatPositionsToStorage(
          (geometry as { coordinates: unknown }).coordinates,
          apiSrid,
          hasZ
        );
      }
      break;
  }

  return {
    type: 'Feature',
    geometry: {
      type: storageType,
      coordinates,
    } as Geometry,
    properties: sketch.properties ?? {},
  };
}

/** Normalize API/read geometry to WGS84 and simplify Multi* types for gl-draw. */
export function featureGeometryForDraw(
  feature: Feature,
  layerType: string
): Feature {
  const normalized = normalizeSketchFeatureToWgs84(feature);
  const g = normalized.geometry;
  if (!g) return normalized;

  switch (layerType) {
    case 'Point':
      if (g.type === 'MultiPoint' && g.coordinates.length > 0) {
        return {
          ...normalized,
          geometry: { type: 'Point', coordinates: g.coordinates[0] },
        };
      }
      break;
    case 'Polyline':
      if (g.type === 'MultiLineString' && g.coordinates.length > 0) {
        return {
          ...normalized,
          geometry: { type: 'LineString', coordinates: g.coordinates[0] },
        };
      }
      break;
    case 'Polygon':
      if (g.type === 'MultiPolygon' && g.coordinates.length > 0) {
        return {
          ...normalized,
          geometry: { type: 'Polygon', coordinates: g.coordinates[0] },
        };
      }
      break;
    default:
      break;
  }

  return normalized;
}
